/**
 * Utilidades compartidas por las tres vistas.
 *
 * Todo lo que hay acá es aritmética de dominios y de metadata: sin estado, sin
 * React y sin llamadas de red, para que se pueda razonar (y testear) sin montar
 * una pantalla.
 */

import type { Domain, DomainNode, FieldMeta, ModelSpec, SearchFilter } from '@/lib/types';

// ---------------------------------------------------------------------------
// Dominios
// ---------------------------------------------------------------------------
/**
 * Une dominios con AND.
 *
 * Concatenar alcanza: el parser del backend combina con AND implícito las
 * expresiones sueltas del nivel superior, así que `[...["|", a, b], c]` es
 * `OR(a,b) AND c`. Eso es exactamente lo que queremos al sumar el filtro de la
 * acción, el del buscador y el de la agrupación.
 */
export function andDomains(...partes: Array<Domain | undefined>): Domain {
  const salida: DomainNode[] = [];
  for (const parte of partes) {
    if (parte === undefined || parte.length === 0) continue;
    salida.push(...parte);
  }
  return salida;
}

/** Une dominios con OR, encadenando el operador prefijo `|` de a pares. */
export function orDomains(partes: Domain[]): Domain {
  const utiles = partes.filter((parte) => parte.length > 0);
  const primera = utiles[0];
  if (primera === undefined) return [];
  if (utiles.length === 1) return primera;

  let acumulado: DomainNode[] = [...primera];
  for (let indice = 1; indice < utiles.length; indice += 1) {
    const siguiente = utiles[indice];
    if (siguiente === undefined) continue;
    acumulado = ['|', ...acumulado, ...siguiente];
  }
  return acumulado;
}

/**
 * Dominio de la búsqueda de texto libre: `ilike` sobre cada campo declarado en
 * la vista de búsqueda, unidos con OR.
 */
export function textSearchDomain(texto: string, campos: string[]): Domain {
  const limpio = texto.trim();
  if (limpio === '' || campos.length === 0) return [];
  return orDomains(campos.map((campo) => [[campo, 'ilike', limpio]] as Domain));
}

/**
 * Dominio de los filtros de un clic activos.
 *
 * Dentro de un mismo `group` van con OR —son alternativas: «clientes o
 * proveedores»— y los grupos entre si van con AND.
 *
 * El `group` existe porque no todos los filtros son alternativas. «Todos los
 * consorcios (no solo el activo)» no es una alternativa, es un ALCANCE: sumado
 * con OR a «Sin administrador» devolvia todos los edificios en vez de los
 * huerfanos de toda la cartera, asi que la pregunta «cuales de mis consorcios
 * quedaron sin administrador» no se podia hacer y la pantalla contestaba
 * «ningun resultado», que se lee como «esta todo bien».
 *
 * Los filtros sin `group` caen todos en el mismo grupo (el vacio), asi que un
 * modelo que no lo declara se comporta exactamente como antes.
 */
export function filtersDomain(filtros: SearchFilter[], activos: Set<string>): Domain {
  const elegidos = filtros.filter((filtro) => activos.has(filtro.name));
  if (elegidos.length === 0) return [];

  // `Map` y no un objeto: conserva el orden de aparicion, asi que el dominio
  // resultante es estable y no depende del orden de las claves.
  const porGrupo = new Map<string, Domain[]>();
  for (const filtro of elegidos) {
    const grupo = filtro.group ?? '';
    const acumulado = porGrupo.get(grupo);
    if (acumulado === undefined) porGrupo.set(grupo, [filtro.domain]);
    else acumulado.push(filtro.domain);
  }

  return andDomains(...[...porGrupo.values()].map((dominios) => orDomains(dominios)));
}

// ---------------------------------------------------------------------------
// Orden
// ---------------------------------------------------------------------------
export interface OrderState {
  field: string;
  desc: boolean;
}

/** Interpreta el `default_order` de la vista lista (`"-created_at"`). */
export function parseOrder(orden: string | null | undefined): OrderState | null {
  if (orden === null || orden === undefined || orden.trim() === '') return null;
  const primero = orden.split(',')[0]?.trim();
  if (primero === undefined || primero === '') return null;
  return primero.startsWith('-') ? { field: primero.slice(1), desc: true } : { field: primero, desc: false };
}

export function serializeOrder(orden: OrderState | null): string | undefined {
  if (orden === null) return undefined;
  return orden.desc ? `-${orden.field}` : orden.field;
}

/** Un click en la cabecera: mismo campo invierte el sentido, otro campo empieza ascendente. */
export function toggleOrder(actual: OrderState | null, campo: string): OrderState {
  if (actual !== null && actual.field === campo) return { field: campo, desc: !actual.desc };
  return { field: campo, desc: false };
}

// ---------------------------------------------------------------------------
// Campos
// ---------------------------------------------------------------------------
/** Solo los campos que existen en la metadata (y que el usuario puede ver). */
export function existingFields(spec: ModelSpec, nombres: string[]): string[] {
  return nombres.filter((nombre) => spec.fields[nombre] !== undefined);
}

/**
 * Divulgación progresiva (ARQUITECTURA.md §7): `priority` 1 es lo que usa el 90%
 * y va a la vista; el resto se guarda detrás de "Opciones avanzadas".
 */
export function splitByPriority(spec: ModelSpec, nombres: string[]): { basicos: string[]; avanzados: string[] } {
  const basicos: string[] = [];
  const avanzados: string[] = [];
  for (const nombre of nombres) {
    const meta = spec.fields[nombre];
    if (meta === undefined) continue;
    if (meta.priority > 1) avanzados.push(nombre);
    else basicos.push(nombre);
  }
  return { basicos, avanzados };
}

/** Campos que hay que pedirle al backend para dibujar estas columnas. */
export function fieldsToFetch(spec: ModelSpec, columnas: string[]): string[] {
  const pedidos = new Set(existingFields(spec, columnas));
  // Las decoraciones evalúan dominios contra la fila: si el campo no viaja, la
  // decoración no se puede evaluar y la fila se vería sin marcar.
  for (const dominio of Object.values(spec.views.list?.decorations ?? {})) {
    for (const token of dominio) {
      // `typeof !== 'string'` y no `Array.isArray`: el término es una tupla de
      // solo lectura y `isArray` no la estrecha bien.
      if (typeof token !== 'string' && spec.fields[token[0]] !== undefined) {
        pedidos.add(token[0]);
      }
    }
  }
  if (spec.fields[spec.rec_name] !== undefined) pedidos.add(spec.rec_name);
  // `active` decide si la fila se muestra atenuada como archivada.
  if (spec.fields.active !== undefined) pedidos.add('active');
  return [...pedidos];
}

/** Etiqueta de una columna, con la metadata como única fuente. */
export function columnLabel(spec: ModelSpec, nombre: string): string {
  return spec.fields[nombre]?.label ?? nombre;
}

/** Los numéricos se alinean a la derecha; el resto a la izquierda. */
export function isNumeric(meta: FieldMeta | undefined): boolean {
  if (meta === undefined) return false;
  return meta.type === 'integer' || meta.type === 'float' || meta.type === 'decimal';
}

// ---------------------------------------------------------------------------
// Bloques de importes
// ---------------------------------------------------------------------------
/**
 * Convierte un importe a centésimos EXACTOS, sin pasar por `number`.
 *
 * Los `DecimalField` viajan como string («9430500.00») justamente para no
 * perder precisión, así que sumarlos con `Number` sería tirar a la basura la
 * única garantía que da el backend. Se escala a entero con `BigInt` sobre el
 * texto: `"1980405.00"` con escala 2 es `198040500n`.
 *
 * `null` si el valor no es un número decimal reconocible (vacío, texto, NaN).
 */
function aEscala(valor: unknown, escala: number): bigint | null {
  const texto =
    typeof valor === 'string' ? valor.trim() : typeof valor === 'number' ? String(valor) : '';
  if (!/^-?\d+(\.\d+)?$/.test(texto)) return null;
  const negativo = texto.startsWith('-');
  const [entera = '0', decimal = ''] = texto.replace('-', '').split('.');
  // Se recorta o se rellena hasta la escala pedida. Recortar sólo puede pasar si
  // el backend manda más decimales de los que declara, y truncar de más es
  // preferible a redondear en silencio dentro de una comparación.
  const ajustada = decimal.slice(0, escala).padEnd(escala, '0');
  const magnitud = BigInt(`${entera}${ajustada}`);
  return negativo ? -magnitud : magnitud;
}

/**
 * Detecta, DENTRO de un bloque de importes, cuál de ellos es la suma de los
 * anteriores, y devuelve la aclaración que hay que dibujarle debajo.
 *
 * Por qué existe: el pie de un pedido de venta muestra «Total $ 11.410.905» y
 * «Facturado $ 9.430.500» uno al lado del otro, y así leído parece que falta
 * plata o que algo se calculó mal. No falta nada —el total lleva impuestos y lo
 * facturado es neto— pero la pantalla no lo decía en ninguna parte y el usuario
 * no tiene por qué deducirlo.
 *
 * La regla es GENÉRICA y se verifica contra los números que hay enfrente, no
 * contra ningún modelo: si la suma exacta de todos los importes anteriores del
 * bloque da el importe actual, entonces ese campo ES la suma y se lo rotula
 * nombrando sus partes («Es la suma de Neto + Impuestos»). Se marca uno solo —el
 * primero que cierra— porque lo que viene después de un total ya no lo compone:
 * es otra cosa, y confundirlas sería el mismo error que estamos arreglando.
 *
 * Nunca inventa: si ninguna suma cierra, no dibuja nada. Y con todo en `$ 0,00`
 * —un pedido recién creado— tampoco dice nada, aunque `0 + 0 = 0` sea cierto:
 * en ceros CUALQUIER trío cierra, así que ahí la coincidencia no prueba que un
 * campo componga al otro. Sin importes no hay confusión que aclarar.
 *
 * @param nombres campos del bloque, EN EL ORDEN en que se dibujan.
 * @param spec metadata del modelo (para la etiqueta y el tipo de cada campo).
 * @param valores valores actuales del formulario.
 * @returns `{campo: aclaración}` con a lo sumo una entrada.
 */
export function amountBreakdown(
  nombres: string[],
  spec: ModelSpec,
  valores: Record<string, unknown>,
): Record<string, string> {
  /** Escala de comparación: la mayor que declare el bloque, con piso en 2. */
  let escala = 2;
  const montos: Array<{ nombre: string; label: string }> = [];
  for (const nombre of nombres) {
    const meta = spec.fields[nombre];
    if (meta === undefined) continue;
    // Sólo importes que ESCRIBE el sistema. Un campo editable no es un total
    // derivado, es un dato que el usuario carga, y rotularlo como suma de otros
    // sería mentirle sobre quién manda.
    if (meta.widget !== 'money' && meta.widget !== 'monetary') continue;
    if (!meta.readonly) continue;
    const digits = meta.digits;
    if (Array.isArray(digits) && typeof digits[1] === 'number') {
      escala = Math.max(escala, digits[1]);
    }
    montos.push({ nombre, label: meta.label });
  }

  // Con menos de tres no hay «suma de»: hacen falta al menos dos sumandos.
  if (montos.length < 3) return {};

  const cantidades = montos.map((monto) => aEscala(valores[monto.nombre], escala));

  let acumulado = 0n;
  for (let indice = 0; indice < montos.length; indice += 1) {
    const actual = cantidades[indice];
    // Un hueco (campo vacío o ilegible) corta la cadena: no se puede afirmar una
    // suma de la que falta un término.
    if (actual === undefined || actual === null) return {};
    if (indice >= 2 && acumulado !== 0n && actual === acumulado) {
      const partes = montos.slice(0, indice).map((monto) => monto.label);
      const entrada = montos[indice];
      if (entrada === undefined) return {};
      return { [entrada.nombre]: `Es la suma de ${partes.join(' + ')}.` };
    }
    acumulado += actual;
  }

  return {};
}

// ---------------------------------------------------------------------------
// Decoraciones
// ---------------------------------------------------------------------------
/**
 * Estilo de fila por decoración. Se aplica al texto y no al fondo: una tabla con
 * filas de cuatro colores de fondo es ilegible, y además el color no puede ser
 * el único portador de información (WCAG 1.4.1) — por eso van en negrita o en
 * cursiva, no solo teñidas.
 */
export const DECORATION_CLASSES: Record<string, string> = {
  danger: 'text-danger font-medium',
  warning: 'text-warning font-medium',
  success: 'text-success',
  info: 'text-accent',
  muted: 'text-fg-faint italic',
  bold: 'font-semibold',
};
